import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api, getFriendlyErrorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const fallbackPlans = [
  {
    code: 'STARTER',
    name: 'Starter',
    monthly_price_inr: 1499,
    max_users: 3,
    max_monthly_coils: 300,
    features_json: ['Owner + 2 users', 'Core coil + billing flow', 'Basic reports'],
    is_recommended: false,
  },
  {
    code: 'STANDARD',
    name: 'Standard',
    monthly_price_inr: 2999,
    max_users: 10,
    max_monthly_coils: 1500,
    features_json: ['Most popular', 'Full production + accounts', 'Priority support'],
    is_recommended: true,
  },
  {
    code: 'PRO',
    name: 'Pro',
    monthly_price_inr: 5999,
    max_users: null,
    max_monthly_coils: null,
    features_json: ['Advanced controls', 'Exports + audit depth', 'Higher limits'],
    is_recommended: false,
  },
];

const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState(fallbackPlans);
  const [form, setForm] = useState({
    company_name: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    password: '',
  });

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await api.subscriptions.listPlans();
        const apiPlans = res?.data?.plans;
        if (Array.isArray(apiPlans) && apiPlans.length > 0) {
          setPlans(apiPlans);
        }
      } catch {
        // fallback stays
      }
    };
    loadPlans();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await signup(form);
      toast.success('Signup successful. 14-day trial started.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err, 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  const renderLimit = (v) => (v === null || v === undefined ? 'Unlimited' : v);

  return (
    <div className="min-h-screen bg-industrial-bg text-[#E0E0E0] p-4 md:p-8 font-sans">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-industrial-s1 border border-industrial-border p-6 rounded-none text-left">
          <div className="mb-6">
            <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-white">Create Owner Account</h2>
            <p className="text-xs font-mono text-slate-400 uppercase tracking-wide mt-1">Start 14-day free trial. No payment needed now.</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="company_name" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Company Name *</Label>
              <Input id="company_name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="owner_name" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Owner Name *</Label>
              <Input id="owner_name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} required className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="owner_email" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Owner Email *</Label>
              <Input id="owner_email" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} required className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="owner_phone" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Phone</Label>
              <Input id="owner_phone" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-400">Password *</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="bg-industrial-s2 border border-industrial-border text-white font-mono rounded-none focus:border-accent focus:ring-0 h-10 w-full" />
            </div>
            
            <button 
              type="submit" 
              className="w-full h-11 bg-accent hover:bg-accent-hover text-white font-mono font-bold uppercase tracking-wider rounded-none border-b-2 border-black/30 transition-colors duration-75 mt-2" 
              disabled={loading}
            >
              {loading ? 'Creating account...' : 'Start Free Trial'}
            </button>
            
            <p className="mt-4 font-mono text-xs text-slate-500 text-center uppercase tracking-wider">
              Already have account? <Link to="/login" className="text-accent underline hover:text-accent-hover ml-1 font-bold">Login</Link>
            </p>
          </form>
        </div>
 
        <div className="space-y-4 text-left">
          <div>
            <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-white">SaaS Plans</h2>
            <p className="text-xs font-mono text-slate-400 uppercase tracking-wide mt-1">Standard plan is the best value for most factories.</p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {plans.map((p) => {
              const isBest = !!p.is_recommended || p.code === 'STANDARD';
              return (
                <div key={p.code} className={`bg-industrial-s1 border p-6 rounded-none ${isBest ? 'border-accent' : 'border-industrial-border'}`}>
                  <div className="flex items-center justify-between border-b border-industrial-border pb-3 mb-3">
                    <div>
                      <h3 className="font-mono text-sm font-bold text-white uppercase">{p.name}</h3>
                      <p className="font-mono text-lg font-bold text-white mt-1">₹{Number(p.monthly_price_inr || 0).toLocaleString('en-IN')}/mo</p>
                    </div>
                    {isBest ? <span className="bg-accent text-black font-mono text-[9px] font-black uppercase py-0.5 px-1.5 rounded-sm">RECOMMENDED</span> : null}
                  </div>
                  <div className="space-y-2 text-xs font-mono text-slate-400">
                    <p><span className="text-[#666]">USERS CAPACITY:</span> {renderLimit(p.max_users)}</p>
                    <p><span className="text-[#666]">MONTHLY COILS LIMIT:</span> {renderLimit(p.max_monthly_coils)}</p>
                    <ul className="list-disc pl-4 space-y-1 font-sans text-slate-300 text-xs">
                      {(p.features_json || []).map((f, idx) => (
                        <li key={idx}>{String(f)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
